import { useState, useCallback, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAuthenticated, isAdmin, logout as apiLogout, restoreSession } from './api/client';
import Layout from './components/Layout';
import { ScreenContextProvider } from './components/ScreenContext';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider } from './components/Toast';
import AppBackConfirmHost from './components/AppBackConfirmHost';
import { ModuleProvider, useModules } from './hooks/useModules';
import { FeatureFlagsProvider } from './hooks/useFeatureFlags';
import Login from './pages/Login';
import Privatumas from './pages/Privatumas';
import Taisykles from './pages/Taisykles';
import WorkspaceGuard from './components/WorkspaceGuard';
import InvoiceList from './pages/invoices/InvoiceList';
import InvoiceDetail from './pages/invoices/InvoiceDetail';
import InvoiceCreate from './pages/invoices/InvoiceCreate';
import InvoiceEdit from './pages/invoices/InvoiceEdit';
import CreditNoteCreate from './pages/invoices/CreditNoteCreate';
import BankingList from './pages/banking/BankingList';
import BankImport from './pages/banking/BankImport';
import PaymentPreparation from './pages/banking/PaymentPreparation';
import SodraDeclarations from './pages/payroll/SodraDeclarations';
import WorkerContracts from './pages/payroll/WorkerContracts';
import WorkTimeSheetPage from './pages/payroll/WorkTimeSheetPage';
import PayrollConfigPage from './pages/payroll/PayrollConfigPage';
import BankAccountsPage from './pages/banking/BankAccountsPage';
import JournalList from './pages/journal/JournalList';
import JournalDetail from './pages/journal/JournalDetail';
import JournalEdit from './pages/journal/JournalEdit';
import AccountList from './pages/accounts/AccountList';
import TrialBalance from './pages/accounts/TrialBalance';
import PersonList from './pages/persons/PersonList';
import PeriodClose from './pages/periods/PeriodClose';
import VatSummary from './pages/reports/VatSummary';
import VatDeclaration from './pages/reports/VatDeclaration';
import FinancialStatements from './pages/reports/FinancialStatements';
import DebtAging from './pages/reports/DebtAging';
import CashBook from './pages/reports/CashBook';
import AccountStatement from './pages/reports/AccountStatement';
import DebtStatement from './pages/reports/DebtStatement';
import ExportSaft from './pages/reports/ExportSaft';
import CatalogList from './pages/catalog/CatalogList';
import Settings from './pages/Settings';
import WorkspacesPage from './pages/workspaces/WorkspacesPage';
import AIAudit from './pages/ai-audit/AIAudit';
import OffsetList from './pages/offsets/OffsetList';
import RoleManager from './pages/settings/RoleManager';
import UsersPage from './pages/settings/UsersPage';
import MyFindings from './pages/findings/MyFindings';
import CloseWizard from './pages/periods/CloseWizard';
import RoutinesPage from './pages/routines/RoutinesPage';
import DataBackup from './pages/settings/DataBackup';
import EmailConfig from './pages/settings/EmailConfig';
import DocumentSerialManager from './pages/settings/DocumentSerialManager';
import CashOperationsList from './pages/cash/CashOperationsList';
import AuditTrail from './pages/audit/AuditTrail';
import Arena from './pages/arena/Arena';
import FindingsBoard from './pages/admin/FindingsBoard';
import AnalyticsPage from './pages/analytics/AnalyticsPage';
import AskPage from './pages/analytics/AskPage';
import PortfolioPage from './pages/darbai/PortfolioPage';
import DocumentsPage from './pages/documents/DocumentsPage';

// Lazy-loaded module pages -- these are only fetched when the user navigates to them
const AdvanceReportList = lazy(() => import('./pages/advance-reports/AdvanceReportList'));
const FixedAssetList = lazy(() => import('./pages/fixed-assets/FixedAssetList'));
const InventoryList = lazy(() => import('./pages/inventory/InventoryList'));
const PayrollList = lazy(() => import('./pages/payroll/PayrollList'));
const ModulesSettings = lazy(() => import('./pages/settings/ModulesSettings'));

// Module route wrapper that checks if a module is active before rendering
function ModuleRoute({ moduleId, children }: { moduleId: string; children: React.ReactNode }) {
  const { isModuleActive } = useModules();
  if (!isModuleActive(moduleId)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// Route wrapper for operator/admin-only surfaces (Arena, AI & Audit).
// The API enforces authorization regardless — this only avoids rendering
// admin UI for client users.
function AdminRoute({ children }: { children: React.ReactNode }) {
  if (!isAdmin()) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// Loading spinner for lazy-loaded modules
function ModuleLoading() {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
      <div style={{ textAlign: 'center', color: '#666' }}>
        <div style={{ fontSize: '24px', marginBottom: '8px' }}>{t('common.loading')}</div>
      </div>
    </div>
  );
}

// The accountant's landing rule: the ACTIVE company's Darbai inbox. Per-company
// isolation is the product decision (the cross-company portfolio confused more
// than it helped); the workspace switcher is the company navigation.
function HomeRedirect() {
  return <Navigate to="/analytics" replace />;
}

// Layout route that wraps its children with WorkspaceGuard.
// The /workspaces route is rendered OUTSIDE the guard so it is always accessible.
function GuardedLayout() {
  return (
    <WorkspaceGuard>
      <Outlet />
    </WorkspaceGuard>
  );
}

export default function App() {
  const [authenticated, setAuthenticated] = useState(isAuthenticated());
  // Bridge logins arrive as full-page redirects: the httpOnly cookie exists but
  // the localStorage hint does not — probe /auth/me once before showing Login,
  // otherwise the user sees a login button that "doesn't work".
  const [booting, setBooting] = useState(!isAuthenticated());

  useEffect(() => {
    let cancelled = false;
    if (isAuthenticated()) return;
    restoreSession()
      .then((ok) => { if (!cancelled && ok) setAuthenticated(true); })
      .finally(() => { if (!cancelled) setBooting(false); });
    return () => { cancelled = true; };
  }, []);

  const handleLogin = useCallback(() => {
    setAuthenticated(true);
  }, []);

  const handleLogout = useCallback(() => {
    apiLogout();
    setAuthenticated(false);
  }, []);

  if (booting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-lg font-semibold text-primary-700">a5suite</div>
      </div>
    );
  }

  if (!authenticated) {
    // FeatureFlagsProvider wraps the login branch too — Login needs the
    // auth-mode flags (bridge vs local) to render the right form.
    // Legal pages are reachable logged-OUT (a privacy link behind a login wall
    // is not a privacy link).
    return (
      <FeatureFlagsProvider>
        <Routes>
          <Route path="/privatumas" element={<Privatumas />} />
          <Route path="/taisykles" element={<Taisykles />} />
          <Route path="*" element={<Login onLogin={handleLogin} />} />
        </Routes>
      </FeatureFlagsProvider>
    );
  }

  return (
    <FeatureFlagsProvider>
    <ModuleProvider>
      <ToastProvider>
        {/* Finding 35: the unsaved-changes confirm opened by useAppBack when
            the browser's Back hits a dirty edit form (single mount point). */}
        <AppBackConfirmHost />
        <ErrorBoundary>
          <ScreenContextProvider>
            <Routes>
              <Route element={<Layout onLogout={handleLogout} />}>
                {/* Workspaces management = operator-only (import client db / create
                    new company). Clients reach their company via the workspace
                    switcher; /workspaces/mine is their listing endpoint. */}
                <Route path="workspaces" element={<AdminRoute><WorkspacesPage /></AdminRoute>} />
                <Route path="privatumas" element={<Privatumas />} />
                <Route path="taisykles" element={<Taisykles />} />

                {/* All other routes are guarded by WorkspaceGuard */}
                <Route element={<GuardedLayout />}>
                  {/* Core routes - always available */}
                  <Route index element={<HomeRedirect />} />
                  <Route path="darbai" element={<PortfolioPage />} />
                  <Route path="analytics" element={<AnalyticsPage />} />
                  <Route path="analytics/ask" element={<AskPage />} />
                  <Route path="documents" element={<DocumentsPage embedded />} />
                  <Route path="uzdarymas" element={<CloseWizard />} />
                  <Route path="automatizavimas" element={<RoutinesPage />} />
                  <Route path="invoices" element={<InvoiceList direction="Made" />} />
                  <Route path="invoices/made" element={<InvoiceList direction="Made" />} />
                  <Route path="invoices/scan" element={<DocumentsPage embedded />} />
                  <Route path="invoices/received" element={<InvoiceList direction="Received" />} />
                  <Route path="invoices/new" element={<InvoiceCreate />} />
                  <Route path="invoices/credit-note/new" element={<CreditNoteCreate />} />
                  <Route path="invoices/:id/edit" element={<InvoiceEdit />} />
                  <Route path="invoices/:id" element={<InvoiceDetail />} />
                  <Route path="banking" element={<BankingList />} />
                  <Route path="banking/accounts" element={<BankAccountsPage />} />
                  <Route path="banking/import" element={<BankImport />} />
          <Route path="banking/payments" element={<PaymentPreparation />} />
                  <Route path="cash-operations" element={<CashOperationsList />} />
                  <Route path="offsets" element={<OffsetList />} />
                  <Route path="journal" element={<JournalList />} />
                  <Route path="journal/new" element={<JournalEdit />} />
                  <Route path="journal/:id/edit" element={<JournalEdit />} />
                  <Route path="journal/:id" element={<JournalDetail />} />
                  <Route path="accounts" element={<AccountList />} />
                  <Route path="accounts/trial-balance" element={<TrialBalance />} />
                  <Route path="persons" element={<PersonList />} />
                  <Route path="periods/close" element={<PeriodClose />} />
                  <Route path="reports/vat" element={<VatSummary />} />
                  <Route path="reports/vat-declaration" element={<VatDeclaration />} />
                  <Route path="reports/financial-statements" element={<FinancialStatements />} />
                  <Route path="reports/debt-aging" element={<DebtAging />} />
                  <Route path="reports/cash-book" element={<CashBook />} />
                  <Route path="reports/account-statement" element={<AccountStatement />} />
                  <Route path="reports/debt-statement" element={<DebtStatement />} />
                  <Route path="reports/saft" element={<ExportSaft />} />
                  <Route path="catalog" element={<CatalogList />} />
                  <Route path="audit" element={<AuditTrail />} />
                  <Route path="ai-audit" element={<AdminRoute><AIAudit /></AdminRoute>} />
                  <Route path="arena" element={<AdminRoute><Arena /></AdminRoute>} />
                  <Route path="admin/findings" element={<AdminRoute><FindingsBoard /></AdminRoute>} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="settings/roles" element={<AdminRoute><RoleManager /></AdminRoute>} />
                  <Route path="mano-uzklausos" element={<MyFindings />} />
          <Route path="settings/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
                  <Route path="settings/modules" element={<AdminRoute><ModulesSettings /></AdminRoute>} />
                  <Route path="settings/backup" element={<AdminRoute><DataBackup /></AdminRoute>} />
                  <Route path="settings/email" element={<AdminRoute><EmailConfig /></AdminRoute>} />
                  <Route path="settings/document-serials" element={<DocumentSerialManager />} />

                  {/* Module routes - lazy loaded, gated by module license */}
                  <Route path="advance-reports" element={
                    <ModuleRoute moduleId="AdvanceReports">
                      <Suspense fallback={<ModuleLoading />}>
                        <AdvanceReportList />
                      </Suspense>
                    </ModuleRoute>
                  } />
                  <Route path="fixed-assets" element={
                    <ModuleRoute moduleId="Assets">
                      <Suspense fallback={<ModuleLoading />}>
                        <FixedAssetList />
                      </Suspense>
                    </ModuleRoute>
                  } />
                  <Route path="inventory" element={
                    <ModuleRoute moduleId="Inventory">
                      <Suspense fallback={<ModuleLoading />}>
                        <InventoryList />
                      </Suspense>
                    </ModuleRoute>
                  } />
                  <Route path="payroll" element={
                    <ModuleRoute moduleId="Payroll">
                      <Suspense fallback={<ModuleLoading />}>
                        <PayrollList />
                      </Suspense>
                    </ModuleRoute>
                  } />
                  <Route path="payroll/declarations" element={
                    <ModuleRoute moduleId="Payroll">
                      <Suspense fallback={<ModuleLoading />}>
                        <SodraDeclarations />
                      </Suspense>
                    </ModuleRoute>
                  } />

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
                  <Route path="payroll/contracts" element={
                    <ModuleRoute moduleId="Payroll">
                      <Suspense fallback={<ModuleLoading />}>
                        <WorkerContracts />
                      </Suspense>
                    </ModuleRoute>
                  } />
              </Route>
                  <Route path="payroll/work-time" element={
                    <ModuleRoute moduleId="Payroll">
                      <Suspense fallback={<ModuleLoading />}>
                        <WorkTimeSheetPage />
                      </Suspense>
                    </ModuleRoute>
                  } />
                  <Route path="payroll/config" element={
                    <ModuleRoute moduleId="Payroll">
                      <Suspense fallback={<ModuleLoading />}>
                        <PayrollConfigPage />
                      </Suspense>
                    </ModuleRoute>
                  } />
            </Routes>
          </ScreenContextProvider>
        </ErrorBoundary>
      </ToastProvider>
    </ModuleProvider>
    </FeatureFlagsProvider>
  );
}
